// Tracking depth (roadmap Batch 7) — the pure helpers: progress (#28), loans
// (#32), price notes (#30) and the shareable wishlist (#31).

// Which units each category's progress can be counted in (first = default).
// TV and anime already track episodes; movies need none.
export const PROGRESS_UNITS = {
	book: ["pages", "%"],
	videogame: ["hours", "%"],
	comic: ["issues", "pages", "%"],
};

export function progressUnits(mediaType) {
	return PROGRESS_UNITS[mediaType] ?? null;
}

// Clean a progress entry: numbers ≥ 0, "%" capped at 100, total ≥ current
// where there is one. → { current, total, unit } or null when empty.
export function normalizeProgress(p, mediaType) {
	const units = progressUnits(mediaType);
	if (!units || !p) return null;
	const unit = units.includes(p.unit) ? p.unit : units[0];
	const num = (v) => {
		if (v == null || String(v).trim() === "") return null;
		const n = Number(String(v).replace(",", "."));
		return Number.isFinite(n) && n >= 0 ? Math.round(n * 10) / 10 : null;
	};
	let current = num(p.current);
	let total = unit === "%" ? 100 : num(p.total);
	if (current == null && (total == null || unit === "%")) return null;
	if (unit === "%") current = Math.min(100, current ?? 0);
	if (total != null && total === 0) total = null;
	if (total != null && current != null && current > total) total = current;
	return { current: current ?? 0, total, unit };
}

// 0–100, or null when there's nothing to measure against (hours, no total).
export function progressPct(p) {
	if (!p || p.total == null || !p.total) return null;
	return Math.max(0, Math.min(100, Math.round((p.current / p.total) * 100)));
}

export function progressLabel(p) {
	if (!p) return "";
	if (p.unit === "%") return `${p.current}%`;
	const unit = p.unit === "hours" ? "h" : p.unit === "pages" ? "p." : "issues";
	if (p.unit === "pages") return p.total ? `p. ${p.current} / ${p.total}` : `p. ${p.current}`;
	if (p.unit === "hours") return p.total ? `${p.current} / ${p.total} h` : `${p.current} h played`;
	return p.total ? `${p.current} / ${p.total} ${unit}` : `${p.current} ${unit}`;
}

// ---------- loans ----------

// A loan: { to, since (YYYY-MM-DD), remind_on (YYYY-MM-DD | null) }.
export function normalizeLoan(l) {
	const to = String(l?.to ?? "").trim().slice(0, 60);
	if (!to) return null;
	const date = (d) => (/^\d{4}-\d{2}-\d{2}$/.test(d ?? "") ? d : null);
	return { to, since: date(l.since), remind_on: date(l.remind_on) };
}

export function loanDays(loan, today) {
	if (!loan?.since) return null;
	return Math.max(0, Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${loan.since}T00:00:00Z`)) / 86400000));
}

// Reminder notices for loans whose reminder day has come. Ids dedupe in the
// bell, so each reminder rings once.
export function loanReminders(items, today, now = new Date().toISOString()) {
	return (items ?? [])
		.filter((i) => i.loan?.remind_on && i.loan.remind_on <= today)
		.map((i) => ({
			id: `loan:${i.id}:${i.loan.remind_on}`,
			type: "loan",
			title: `Lent out: ${i.title}`,
			body: `${i.loan.to} has had it${i.loan.since ? ` since ${i.loan.since}` : ""} — time to ask for it back?`,
			route: i.route ?? null,
			cover_url: i.cover_url ?? null,
			media_type: i.media_type ?? null,
			item_id: i.id,
			created_at: now,
			read: false,
		}));
}

// ---------- wishlist ----------

const CATEGORY_NAME = { videogame: "Games", book: "Books", tv: "TV", anime: "Anime", movie: "Movies", comic: "Comics" };
const ORDER = ["videogame", "book", "movie", "tv", "anime", "comic"];

// The shareable wishlist as plain text: by category, with your price notes.
export function wishlistText(items, { title = "My wishlist" } = {}) {
	const lines = [title, ""];
	for (const media of ORDER) {
		const group = (items ?? []).filter((i) => i.media_type === media);
		if (!group.length) continue;
		lines.push(`${CATEGORY_NAME[media]}:`);
		for (const i of group) {
			const year = i.release_year ?? i.premiered_year ?? i.published_year ?? null;
			lines.push(`• ${i.title}${year ? ` (${year})` : ""}${i.price_note ? ` — ${i.price_note}` : ""}`);
		}
		lines.push("");
	}
	return lines.join("\n").trim() + "\n";
}
